// Mood Tracker Service - Central service for all mood tracking operations

import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { 
  formatMoodDataForAPI, 
  formatChartData, 
  filterCommentsByMood, 
  getMoodStats,
  calculateDateRange,
  formatDateForAPI
} from '../utils/moodTrackerUtils';

class MoodTrackerService {
  constructor() {
    this.baseUrl = '/.netlify/functions';
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  // Note: Token is now automatically retrieved from storage via api.js

  // Cache management
  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  getCache(key) {
    const cached = this.cache.get(key);
    if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  clearCache() {
    this.cache.clear();
  }

  // Clear specific cache entries by pattern
  clearCacheByPattern(pattern) {
    const keysToDelete = [];
    for (const key of this.cache.keys()) {
      if (key.includes(pattern)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => this.cache.delete(key));
    console.log(`Cleared ${keysToDelete.length} cache entries matching pattern: ${pattern}`);
  }

  /**
   * Format date for display in user's local timezone
   * @param {string|Date} dateInput - Date string or Date object
   * @returns {string} Formatted date string
   */
  formatDateForDisplay(dateInput) {
    if (!dateInput) return 'N/A';
    
    try {
      const date = new Date(dateInput);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  }

  /**
   * Get local date string from UTC timestamp
   * @param {string|Date} utcTimestamp - UTC timestamp
   * @returns {string} Local date string (YYYY-MM-DD)
   */
  getLocalDateString(utcTimestamp) {
    if (!utcTimestamp) return '';
    
    try {
      const date = new Date(utcTimestamp);
      if (isNaN(date.getTime())) return '';
      
      // Get local date components
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error getting local date string:', error);
      return '';
    }
  }

  // Submit new mood entry
  async submitMood(moodData, user = null) {
    try {
      const formattedData = formatMoodDataForAPI(moodData);
      
      const response = await apiPost(
        `${this.baseUrl}/submit-mood`,
        formattedData
      );

      // Clear cache to ensure fresh data
      this.clearCache();
      
      return response;
    } catch (error) {
      console.error('Error submitting mood:', error);
      throw new Error(`Failed to submit mood: ${error.message}`);
    }
  }

  // Get mood history for a specific time range
  async getMoodHistory(timeRange = 'month', user = null) {
    try {
      const cacheKey = `mood-history-${timeRange}`;
      const cached = this.getCache(cacheKey);
      
      if (cached) {
        console.log(`Using cached mood history for ${timeRange}:`, cached.length, 'entries');
        return cached;
      }

      console.log(`Fetching fresh mood history for ${timeRange}`);
      const dateRange = calculateDateRange(timeRange);
      console.log(`Date range: ${dateRange.startDate ? dateRange.startDate.toISOString().split('T')[0] : 'no start'} to ${dateRange.endDate ? dateRange.endDate.toISOString().split('T')[0] : 'no end'}`);
      const params = new URLSearchParams();
      
      if (dateRange.startDate) {
        params.append('startDate', formatDateForAPI(dateRange.startDate));
      }
      if (dateRange.endDate) {
        params.append('endDate', formatDateForAPI(dateRange.endDate));
      }

      const response = await apiGet(
        `${this.baseUrl}/get-mood-history?${params.toString()}`
      );

      const formattedData = formatChartData(response.data || []);
      console.log(`Caching mood history for ${timeRange}:`, formattedData.length, 'entries');
      this.setCache(cacheKey, formattedData);
      
      return formattedData;
    } catch (error) {
      console.error('Error fetching mood history:', error);
      throw new Error(`Failed to fetch mood history: ${error.message}`);
    }
  }

  // Get mood comments filtered by mood type
  async getMoodComments(moodFilter = 'all', timeRange = 'month', user = null) {
    try {
      const cacheKey = `mood-comments-${moodFilter}-${timeRange}`;
      const cached = this.getCache(cacheKey);
      
      if (cached) {
        return cached;
      }

      const moodHistory = await this.getMoodHistory(timeRange, user);
      const filteredComments = filterCommentsByMood(moodHistory, moodFilter);
      
      this.setCache(cacheKey, filteredComments);
      return filteredComments;
    } catch (error) {
      console.error('Error fetching mood comments:', error);
      throw new Error(`Failed to fetch mood comments: ${error.message}`);
    }
  }

  // Get mood statistics
  async getMoodStats(timeRange = 'month', user = null) {
    try {
      const cacheKey = `mood-stats-${timeRange}`;
      const cached = this.getCache(cacheKey);
      
      if (cached) {
        return cached;
      }

      const moodHistory = await this.getMoodHistory(timeRange, user);
      const stats = getMoodStats(moodHistory);
      
      this.setCache(cacheKey, stats);
      return stats;
    } catch (error) {
      console.error('Error fetching mood stats:', error);
      throw new Error(`Failed to fetch mood stats: ${error.message}`);
    }
  }

  // Check if mood has been submitted in the past hour
  async isMoodSubmittedToday(user = null) {
    try {
      // Note: Function name kept for backward compatibility, but now checks past hour
      const response = await apiGet(
        `${this.baseUrl}/check-mood-today`
      );
      
      return response.hasSubmitted || false;
    } catch (error) {
      console.error('Error checking mood submission:', error);
      return false; // Default to false if check fails
    }
  }

  // Get today's mood entry
  async getTodaysMood(user = null) {
    try {
      const today = formatDateForAPI(new Date());
      const response = await apiGet(
        `${this.baseUrl}/get-todays-mood?date=${today}`
      );
      
      return response.data || null;
    } catch (error) {
      console.error('Error fetching today\'s mood:', error);
      return null;
    }
  }

  // Update existing mood entry
  async updateMood(moodId, moodData, user = null) {
    try {
      const formattedData = formatMoodDataForAPI(moodData);
      
      const response = await apiPut(
        `${this.baseUrl}/update-mood/${moodId}`,
        formattedData
      );

      // Clear cache to ensure fresh data
      this.clearCache();
      
      return response;
    } catch (error) {
      console.error('Error updating mood:', error);
      throw new Error(`Failed to update mood: ${error.message}`);
    }
  }

  // Delete mood entry
  async deleteMood(moodId, user = null) {
    try {
      const response = await apiDelete(
        `${this.baseUrl}/delete-mood/${moodId}`,
        {}
      );

      // Clear cache to ensure fresh data
      this.clearCache();
      
      return response;
    } catch (error) {
      console.error('Error deleting mood:', error);
      throw new Error(`Failed to delete mood: ${error.message}`);
    }
  }

  // Get mood history for a specific patient (therapist view)
  async getPatientMoodHistory(patientId, timeRange = 'month', user = null) {
    try {
      const cacheKey = `patient-mood-history-${patientId}-${timeRange}`;
      const cached = this.getCache(cacheKey);
      
      if (cached) {
        console.log(`Using cached patient mood history for ${patientId} (${timeRange}):`, cached.length, 'entries');
        return cached;
      }

      console.log(`Fetching fresh patient mood history for ${patientId} (${timeRange})`);
      const dateRange = calculateDateRange(timeRange);
      const params = new URLSearchParams();
      
      params.append('patientId', patientId);
      if (dateRange.startDate) {
        params.append('startDate', formatDateForAPI(dateRange.startDate));
      }
      if (dateRange.endDate) {
        params.append('endDate', formatDateForAPI(dateRange.endDate));
      }

      const response = await apiGet(
        `${this.baseUrl}/get-patient-mood-history?${params.toString()}`
      );

      const formattedData = formatChartData(response.data || []);
      console.log(`Caching patient mood history for ${patientId} (${timeRange}):`, formattedData.length, 'entries');
      this.setCache(cacheKey, formattedData);
      
      return formattedData;
    } catch (error) {
      console.error('Error fetching patient mood history:', error);
      throw new Error(`Failed to fetch patient mood history: ${error.message}`);
    }
  }

  // Get patient mood comments (therapist view)
  async getPatientMoodComments(patientId, moodFilter = 'all', timeRange = 'month', user = null) {
    try {
      const cacheKey = `patient-mood-comments-${patientId}-${moodFilter}-${timeRange}`;
      const cached = this.getCache(cacheKey);
      
      if (cached) {
        return cached;
      }

      const moodHistory = await this.getPatientMoodHistory(patientId, timeRange, user);
      const filteredComments = filterCommentsByMood(moodHistory, moodFilter);
      
      this.setCache(cacheKey, filteredComments);
      return filteredComments;
    } catch (error) {
      console.error('Error fetching patient mood comments:', error);
      throw new Error(`Failed to fetch patient mood comments: ${error.message}`);
    }
  }

  // Get patient mood statistics (therapist view)
  async getPatientMoodStats(patientId, timeRange = 'month', user = null) {
    try {
      const cacheKey = `patient-mood-stats-${patientId}-${timeRange}`;
      const cached = this.getCache(cacheKey);
      
      if (cached) {
        return cached;
      }

      const moodHistory = await this.getPatientMoodHistory(patientId, timeRange, user);
      const stats = getMoodStats(moodHistory);
      
      this.setCache(cacheKey, stats);
      return stats;
    } catch (error) {
      console.error('Error fetching patient mood stats:', error);
      throw new Error(`Failed to fetch patient mood stats: ${error.message}`);
    }
  }
}

// Create and export singleton instance
const moodTrackerService = new MoodTrackerService();
export default moodTrackerService;
